import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert as ShadcnAlert, AlertDescription } from '@/components/ui/alert';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AlertProps {
  type?: 'success' | 'error' | 'warning' | 'info';
  message: string;
  onClose?: () => void;
  autoClose?: boolean;
  duration?: number;
}

const Alert: React.FC<AlertProps> = ({
  type = 'info',
  message,
  onClose,
  autoClose = false,
  duration = 3000,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  const typeStyles = {
    success: 'border-green-600/40 text-green-700 bg-green-50 dark:border-green-600/30 dark:text-green-400 dark:bg-green-950/30',
    warning: 'border-amber-600/40 text-amber-700 bg-amber-50 dark:border-amber-600/30 dark:text-amber-400 dark:bg-amber-950/30',
    info: 'border-primary/40 text-primary bg-primary/5',
    error: '',
  };

  return (
    <ShadcnAlert
      variant={type === 'error' ? 'destructive' : 'default'}
      className={cn(type !== 'error' && typeStyles[type], 'relative')}
    >
      <AlertDescription className="flex justify-between items-center w-full">
        <span>{message}</span>
        {onClose && (
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="ml-4 inline-flex items-center justify-center rounded-md p-1 hover:bg-foreground/5"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </AlertDescription>
    </ShadcnAlert>
  );
};

export default Alert;
